import React, { useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { isLoggedIn } from '@saviynt/common';
import { OverlayProvider } from '@saviynt/design-system';
import { initialiseEnv } from 'ui-common/src/lib/utils';
import { BrowserRouter as Router } from 'react-router-dom';
import MfeManager from './MicroFrontEnd/Component/MfeManager';
import {
  appConstantsAction,
  appConstantsSuccess,
  getHeaderDetails,
  appMessages,
  userModulesAction,
  userDetailsAction,
  consolidatedConfigAction,
} from 'ui-common/src/utils/LoginActions';
import { APP_CONSTANTS } from 'ui-common/src/constants';
import SessionStorageService from 'ui-common/src/utils/SessionStorageService';
import ConnectedResponseMessageHandling from './misc/ResponseHandler/ResponseHandler';
import { useQuery } from './misc/hooks';
import ConnectedLoadingHandling from './misc/Loading/Loading';
import store from './misc/store';
import themes from './themes';
import './AppHeader.css';
import {
  fetchThemeConfigList,
  getActiveFavIcon,
  getActiveHeaderLogo,
  getActiveLogo,
  getActiveTheme,
} from './features/AdminHome/redux/adminHome-actions';
import SessionTimeoutModal from './logout/SessionTimeoutModal';
import useOmniflowFeatureFlag from './MicroFrontEnd/hooks/useOmniflowFeatureFlag';

import { fetchECMVersion } from './features/MainLayout/Version/redux/version-actions';
import Layout from './Layout/Layout';
import { Header } from './features/MainLayout/Header';

const LeftNavGen2State = {
  unknown: 'unknown',
  enabled: 'enabled',
  disabled: 'disabled',
};

function getLastNavGen2State() {
  const leftNavGen2 = window.localStorage.getItem('leftNavGen2');

  if (leftNavGen2 === null) {
    return LeftNavGen2State.unknown;
  }

  if (leftNavGen2 === '1') {
    return LeftNavGen2State.enabled;
  }

  return LeftNavGen2State.disabled;
}

const HeaderType = () => {
  const leftNavGen2 = useSelector((state) => state.user.leftNavGen2);
  let leftNavGen2State = LeftNavGen2State.unknown;

  if (leftNavGen2 === null) {
    leftNavGen2State = getLastNavGen2State();
  } else if (leftNavGen2 === '1') {
    leftNavGen2State = LeftNavGen2State.enabled;
  } else {
    leftNavGen2State = LeftNavGen2State.disabled;
  }

  if (leftNavGen2State === LeftNavGen2State.enabled) {
    return <Layout />;
  } else if (leftNavGen2State === LeftNavGen2State.disabled) {
    return <Header />;
  }

  return null;
};

const AppHeaderBody = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  // const { THEME } = useSelector(state => state.user.domainInfo) || { THEME: 'defaultTheme' };
  const messages = useSelector((state) => state.user.messages) || {};
  const envConfig = useSelector((state) => state.user.domainInfo);
  const rawSideMenu = useSelector((state) => state.app?.rawSideMenu);
  // let { cssTheme, muiTheme } = themes[THEME];
  // const THEME_COLORS = useSelector(state => state.user.rgb);
  // const { RGB_THEME_COLORS } = useSelector(state => state.user.domainInfo) || {
  //   RGB_THEME_COLORS: THEME_COLORS,
  // };
  const [jsonEditorTheme, setJsonEditorTheme] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [previewTheme, setPreviewTheme] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [configThemeObj, setThemeObj] = useState({});
  const { defaultThemeConfig } = useSelector((state) => state.adminHome.uiConfig);
  // const THEME_COLOR = themes(RGB_THEME_COLORS);
  const domainName = query.get('domain') || window.location.hostname.split('.')[0];

  if (query.get('DEMO_MODE')) window.DEMO_MODE = true;
  // useEffect(() => {
  //   ({ cssTheme, muiTheme } = themes[THEME]);
  //   Object.keys(cssTheme).forEach(key => {
  //     const value = cssTheme[key];
  //     document.documentElement.style.setProperty(key, value);
  //   });
  // }, [THEME]);
  // useEffect(() => {
  //   // ({ cssTheme, muiTheme } = themes[THEME] || themes.defaultTheme);
  //   // &Nagesh, THEME_COLOR is already locked line 82. how change in RGB_THEME_COLORS affect output of following code.
  //   Object.keys(THEME_COLOR.cssTheme).forEach(key => {
  //     const value = THEME_COLOR.cssTheme[key];
  //     document.documentElement.style.setProperty(key, value);
  //   });
  // }, [RGB_THEME_COLORS]);

  useEffect(() => {
    const sessionItems = SessionStorageService.get(APP_CONSTANTS);
    const appConstants = sessionItems && sessionItems[domainName];

    if (appConstants && Object.keys(appConstants).length) {
      dispatch(appConstantsSuccess(appConstants));
    } else {
      dispatch(appConstantsAction(domainName));
    }
  }, []);

  useEffect(() => {
    const serializedState = JSON.parse(localStorage.getItem('uiConfigTheme'));
    const serializedStatePreview = JSON.parse(localStorage.getItem('uiConfigThemeModal'));

    // ({ cssTheme, muiTheme } = themes[THEME] || themes.defaultTheme);
    // &Nagesh, THEME_COLOR is already locked line 82. how change in RGB_THEME_COLORS affect output of following code.

    if (
      serializedState &&
      Object.keys(serializedState).length > 0 &&
      Object.keys(serializedState.previewTheme).length > 0 &&
      serializedStatePreview.showPreviewTheme
    ) {
      const preview = themes(JSON.parse(serializedState.previewTheme));

      Object.keys(preview.cssTheme).forEach((key) => {
        const value = preview.cssTheme[key];

        document.documentElement.style.setProperty(key, value);
      });
      setJsonEditorTheme(preview);
      setPreviewTheme(true);
      setThemeObj(JSON.parse(serializedState.previewTheme));
    } else if (defaultThemeConfig && Object.keys(defaultThemeConfig).length) {
      const THEME_COLOR = themes(defaultThemeConfig);

      Object.keys(THEME_COLOR.cssTheme).forEach((key) => {
        const value = THEME_COLOR.cssTheme[key];

        document.documentElement.style.setProperty(key, value);
      });
      setJsonEditorTheme(THEME_COLOR);
      setPreviewTheme(false);
      setThemeObj(defaultThemeConfig);
    }
  }, [defaultThemeConfig]);

  useEffect(() => {
    initialiseEnv(envConfig);
    dispatch(userModulesAction());

    if (!envConfig || !Object.keys(envConfig).length) {
      return;
    }

    dispatch(consolidatedConfigAction());
    dispatch(fetchECMVersion());
    dispatch(getHeaderDetails(envConfig.GATEWAY_API_URL));
    dispatch(userDetailsAction());
    dispatch(fetchThemeConfigList());
    dispatch(getActiveTheme());
    dispatch(getActiveLogo());
    dispatch(getActiveHeaderLogo());
    dispatch(getActiveFavIcon());
    dispatch(
      appMessages(
        query.get('locale') || localStorage.getItem('locale') || 'en',
        envConfig.GATEWAY_API_URL
      )
    );
  }, [envConfig]);

  useOmniflowFeatureFlag();

  // Dont show ugly messsage keys.
  if (Object.keys(messages).length === 0) {
    return null;
  }

  return (
    <IntlProvider locale="en" messages={messages}>
      <OverlayProvider>
        <div className="main-container">
          <MuiThemeProvider theme={jsonEditorTheme.muiTheme}>
            <Router basename="/ECMv6">
              {rawSideMenu?.length > 0 && <MfeManager />}
              <HeaderType />
              {/* <Layout /> */}
              {/* <Footer /> */}
            </Router>
            <ConnectedResponseMessageHandling />
            <ConnectedLoadingHandling />
            {isLoggedIn() && <SessionTimeoutModal />}
          </MuiThemeProvider>
        </div>
      </OverlayProvider>
    </IntlProvider>
  );
};
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

const AppHeader = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AppHeaderBody />
    </QueryClientProvider>
  </Provider>
);

export default AppHeader;
